'use client';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import cl from 'clsx/lite';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { useDebounceCallback } from '../../utilities/hooks/deprecated/use-debounce-callback/use-debounce-callback.js';
import '@digdir/designsystemet-web';
import '../../utilities/roving-focus/roving-focus-item.js';
import '../../utilities/roving-focus/roving-focus-root.js';
import { Spinner } from '../spinner/spinner.js';
import { ComboboxContext } from './ComboboxContext.js';
import { ComboboxIdProvider } from './ComboboxIdContext.js';
import { ComboboxCustom } from './Custom.js';
import ComboboxError from './internal/ComboboxError.js';
import ComboboxInput from './internal/ComboboxInput.js';
import ComboboxLabel from './internal/ComboboxLabel.js';
import ComboboxNative from './internal/ComboboxNative.js';
import { omit } from './omit/omit.js';
import { useCombobox } from './useCombobox.js';
import { useComboboxKeyboard } from './useComboboxKeyboard.js';
import { useFloatingCombobox } from './useFloatingCombobox.js';
import { useFormField } from './useFormField/useFormField.js';
import { prefix, setReactInputValue, removePrefix } from './utilities.js';

/**
 * @deprecated Use `Suggestion` instead
 */
const ComboboxComponent = forwardRef(({ value, initialValue = [], onValueChange, label, hideLabel = false, description, multiple = false, disabled = false, readOnly = false, hideChips = false, clearButtonLabel = 'Fjern alt', hideClearButton = false, error, errorId, id, name, portal = true, htmlSize = 0, virtual = false, children, style, size = 'md', loading, loadingLabel = 'Laster...', filter, chipSrLabel = (option) => 'Slett ' + option.label, className, ...rest }, forwareddRef) => {
    const inputRef = useRef(null);
    const portalRef = useRef(null);
    const listRef = useRef([]);
    const [inputValue, setInputValue] = useState(rest.inputValue || '');
    useEffect(() => {
        if (typeof rest.inputValue === 'string') {
            setInputValue(rest.inputValue);
        }
    }, [rest.inputValue]);
    const { selectedOptions, options, restChildren, interactiveChildren, customIds, filteredOptionsChildren, filteredOptions, setSelectedOptions, } = useCombobox({
        children,
        inputValue,
        filter,
        multiple,
        initialValue,
    });
    const { open, setOpen, refs, floatingStyles, context, getReferenceProps, getFloatingProps, getItemProps, } = useFloatingCombobox({
        listRef,
    });
    const formFieldProps = useFormField({
        disabled,
        error,
        errorId,
        size,
        description,
        id,
    }, 'combobox');
    // if value is set, set input value to the label of the value
    useEffect(() => {
        if (value && value.length > 0 && !multiple) {
            const option = options[prefix(value[0])];
            inputRef.current &&
                setReactInputValue(inputRef.current, option?.label || '');
        }
    }, [multiple, value, options]);
    useEffect(() => {
        if (value && Object.keys(options).length >= 0) {
            const updatedSelectedOptions = value.map((option) => {
                const value = options[prefix(option)];
                return value;
            });
            setSelectedOptions(updatedSelectedOptions.reduce((acc, value) => {
                acc[prefix(value.value)] = value;
                return acc;
            }, {}));
        }
    }, [multiple, value, options, setSelectedOptions]);
    // handle click on option, either select or deselect - Handles single or multiple
    const handleSelectOption = (args) => {
        const { option, clear, remove } = args;
        if (clear) {
            setSelectedOptions({});
            inputRef.current && setReactInputValue(inputRef.current, '');
            onValueChange?.([]);
            return;
        }
        if (!option)
            return;
        if (remove) {
            const newSelectedOptions = { ...selectedOptions };
            delete newSelectedOptions[prefix(option.value)];
            setSelectedOptions(newSelectedOptions);
            onValueChange?.(Object.keys(newSelectedOptions).map((key) => removePrefix(key)));
            return;
        }
        const newSelectedOptions = { ...selectedOptions };
        if (multiple) {
            if (newSelectedOptions[prefix(option.value)]) {
                delete newSelectedOptions[prefix(option.value)];
            }
            else {
                newSelectedOptions[prefix(option.value)] = option;
            }
            inputRef.current && setReactInputValue(inputRef.current, '');
            inputRef.current?.focus();
        }
        else {
            /* clear newSelectedOptions */
            for (const key of Object.keys(newSelectedOptions)) {
                delete newSelectedOptions[key];
            }
            newSelectedOptions[prefix(option.value)] = option;
            inputRef.current &&
                setReactInputValue(inputRef.current, option?.label || '');
            // move cursor to the end of the input
            setTimeout(() => {
                inputRef.current?.setSelectionRange(option?.label?.length || 0, option?.label?.length || 0);
            }, 0);
        }
        setSelectedOptions(newSelectedOptions);
        onValueChange?.(Object.keys(newSelectedOptions).map((key) => removePrefix(key)));
        !multiple && setOpen(false);
        refs.domReference.current?.focus();
    };
    const debouncedHandleSelectOption = useDebounceCallback(handleSelectOption, 50);
    const handleKeyDown = useComboboxKeyboard({
        filteredOptions,
        selectedOptions,
        readOnly: formFieldProps.readOnly || false,
        disabled: disabled,
        multiple,
        inputValue,
        options,
        open,
        interactiveChildren,
        setOpen,
        handleSelectOption: debouncedHandleSelectOption,
    });
    const rowVirtualizer = useVirtualizer({
        count: Object.keys(filteredOptionsChildren).length,
        getScrollElement: () => (virtual ? refs.floating.current : null),
        estimateSize: () => 70,
        measureElement: (elem) => {
            return elem.getBoundingClientRect().height;
        },
        overscan: 7,
    });
    return (jsxs(ComboboxContext.Provider, { value: {
            size,
            options,
            selectedOptions,
            multiple,
            disabled,
            readOnly,
            open,
            inputRef,
            refs,
            inputValue,
            formFieldProps,
            htmlSize,
            clearButtonLabel,
            customIds,
            filteredOptions,
            setInputValue,
            setOpen,
            getReferenceProps,
            getItemProps,
            /* Recieves the value of the option, and searches for it in our values lookup */
            onOptionClick: (value) => {
                if (readOnly)
                    return;
                if (disabled)
                    return;
                const option = options[prefix(value)];
                debouncedHandleSelectOption({ option: option });
            },
            handleSelectOption: debouncedHandleSelectOption,
            chipSrLabel,
            listRef,
            forwareddRef,
            setListRef: (index, node) => {
                listRef.current[index] = node;
            },
        }, children: [jsxs("div", { className: cl('ds-combobox', `ds-combobox--${size}`, disabled && 'ds-combobox__disabled', className), style: style, ref: portalRef, children: [name && (jsx(ComboboxNative, { name: name, selectedOptions: selectedOptions, multiple: multiple })), jsx(ComboboxLabel, { label: label, description: description, size: size, readOnly: readOnly, hideLabel: hideLabel, formFieldProps: formFieldProps }), jsx(ComboboxInput, { ...omit(['inputValue'], rest), hideClearButton: hideClearButton, listId: context.floatingId || '', error: error, hideChips: hideChips, handleKeyDown: handleKeyDown, "aria-busy": loading }), jsx(ComboboxError, { size: size, error: error, formFieldProps: formFieldProps })] }), open && (jsx(FloatingPortal, { root: portal ? null : portalRef, children: jsx(FloatingFocusManager, { context: context, initialFocus: -1, visuallyHiddenDismiss: true, children: jsxs("div", { "aria-labelledby": formFieldProps.inputProps.id, "aria-autocomplete": 'list', tabIndex: -1, ...getFloatingProps({
                            ref: refs.setFloating,
                            style: {
                                ...floatingStyles,
                            },
                        }), className: cl('ds-combobox__options-wrapper', `ds-combobox--${size}`), children: [virtual && (jsx("div", { style: {
                                    height: `${rowVirtualizer.getTotalSize()}px`,
                                    width: '100%',
                                    position: 'relative',
                                }, children: rowVirtualizer.getVirtualItems().map((virtualRow) => (jsx("div", { ref: rowVirtualizer.measureElement, "data-index": virtualRow.index, style: {
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        transform: `translateY(${virtualRow.start}px)`,
                                    }, children: filteredOptionsChildren[virtualRow.index] }, virtualRow.index))) })), loading ? (jsxs(ComboboxCustom, { className: 'ds-combobox__loading', children: [jsx(Spinner, { "aria-label": 'Laster', "data-size": 'sm' }), loadingLabel] })) : (jsxs(Fragment, { children: [restChildren, !virtual && filteredOptionsChildren] }))] }) }) }))] }));
});
const Combobox = forwardRef((props, ref) => (jsx(ComboboxIdProvider, { children: jsx(ComboboxComponent, { ...props, ref: ref }) })));
Combobox.displayName = 'Combobox';

export { Combobox, ComboboxComponent };
